import { useEffect } from "react";

// What a press on the page behind a modal surface means — the donor's MUI
// `Modal` backdrop, which Radix does not draw. Hoisted out of
// `icon-menu-button.tsx` (#479) when `MuiPopover` became the second surface to
// need it (#690): the item menus and every MUI-shaped popover close on the same
// rule, so the rule lives once.

/**
 * The two shapes every Radix surface that can hold `document.body`'s
 * `pointer-events: none` comes in — a popper (menu, submenu, select, popover,
 * hovercard, tooltip; all portalled inside this wrapper) or a dialog. Nothing
 * else in this app writes that property, so between them these two selectors
 * enumerate the holders exhaustively: both the question "was this press on the
 * page?" and the question "is anything still holding the page?" are asked
 * against them, which is why they are named once here.
 */
export const POPPER_LAYER = "[data-radix-popper-content-wrapper]";
export const DIALOG_LAYER = "[role=dialog],[role=alertdialog]";

/**
 * A modal's viewport-sized root, around its dialog: the mobile date picker's
 * (`mui-mobile-picker-dialog.tsx`), which draws MUI's own backdrop and judges
 * a press on it itself. It holds the page only through the dialog inside it,
 * so it answers the press question alone — a press on it landed on a layer
 * above the page, not on the page.
 */
export const MODAL_ROOT = "[data-modal-root]";

/**
 * Did this press land where the donor's `Backdrop` would have caught it?
 *
 * MUI asks a structural question because it has a real element to ask it of: an
 * invisible, viewport-covering `Backdrop` sits above the page and below the
 * paper (`Backdrop.js:39-47`, `Popover.js:333-337` forcing `invisible`), so
 * "outside the menu" is exactly "on the backdrop" (`useModal.js:125-133`) and
 * nothing on the page beneath can be hit at all while the modal is up.
 *
 * Radix draws no backdrop, so the equivalent question is asked of the layers
 * themselves: a press is a backdrop press when it landed inside no layer — no
 * popper wrapper (this menu's own paper, a submenu, a `Select`'s listbox, one of
 * the rows' reason boxes) and no dialog. Everything else is page, and the donor
 * would have absorbed it.
 *
 * **This used to ask whether the target *was* `html` or `body`**, which agrees
 * with the above everywhere the page is inert — which, while the menu holds it,
 * is everywhere but an element that sets `pointer-events: auto` back on itself.
 * A press on one of those reached the element, answered "not the backdrop", and
 * left the menu standing where the donor's would have gone. Raised by #479's
 * cross-review; the pin below it is now a guard.
 */
export function pressedOnBackdrop(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  return target.closest(`${POPPER_LAYER},${DIALOG_LAYER},${MODAL_ROOT}`) === null;
}

/**
 * **The release dismisses this menu, not the press** (#479 item 2).
 *
 * MUI hangs dismissal on the invisible Backdrop's `onClick`
 * (`useModal.js:157-163`), guarded by `event.target !== event.currentTarget`
 * (`:125-133`); there is no `mousedown` or `pointerdown` path anywhere in it.
 * So the donor's menu stands its ground under a held press, and a press that
 * begins on the page but *ends* on a menu row leaves it open with nothing
 * chosen — the click the browser generates fires at the nearest common
 * ancestor of the two, which for MUI's sibling backdrop and paper is the modal
 * root, and the backdrop's own handler never sees it. A user who presses on
 * the page, changes their mind and slides onto a row gets the menu back rather
 * than a dismissal they did not ask for.
 *
 * Radix closes on `pointerdown` instead, through `DismissableLayer`. That the
 * difference is *visible* took some finding: the exiting paper stays mounted
 * for the whole Grow-out, so for ~200ms after the press a dismissed menu and a
 * standing one look identical in the DOM. The tests therefore read the
 * trigger's `aria-expanded`, which flips on the decision rather than on the
 * animation.
 *
 * The rule cannot be ported structurally, only semantically. MUI discriminates
 * by *what the click's target is*, which works because its backdrop is a
 * sibling of the paper; here the backdrop is the document element, an ancestor
 * of the menu, so every click's target is "the backdrop" and the test would
 * dismiss on everything. Both ends of the gesture are therefore tracked
 * instead, which is what MUI's common-ancestor rule amounts to: the press and
 * the release must *both* land on the page.
 *
 * On `pointerup` rather than `click` because Radix has already been declined
 * at `pointerdown` and the pair is what carries the decision; the two moments
 * are a microtask apart and no screen can tell them apart.
 *
 * And the primary button only, which is the last thing `click` was carrying
 * for the donor: the browser generates no `click` for a secondary press, so a
 * right-click beside a donor menu raises the context menu and leaves the menu
 * standing. `pointerdown`/`pointerup` fire for every button, so the button has
 * to be read back explicitly. A non-primary press is ignored outright rather
 * than recorded as a press that failed the test — a right-click in the middle
 * of a held primary press must not eat the release that primary press is
 * owed.
 */
export function useBackdropPressDismiss(open: boolean, close: (() => void) | undefined) {
  useEffect(() => {
    if (!open || !close) return;
    // **The two ends have to belong to the same pointer**, which is the last
    // thing a `click` carries that a raw press/release pair does not: the
    // browser fires one only for a coherent gesture by a single pointer, and
    // fires none at all once that pointer has been cancelled. Held as the id
    // rather than as a flag, so a second finger's release cannot complete a
    // press the first finger began, and a `pointercancel` — a touch that
    // becomes a scroll, a pen that leaves range — disarms it the way it
    // cancels the donor's `click`. Raised by #479's cross-review, run 3.
    let pressBeganOnBackdrop: number | null = null;
    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      pressBeganOnBackdrop = pressedOnBackdrop(event.target) ? event.pointerId : null;
    };
    const onPointerUp = (event: PointerEvent) => {
      if (event.button !== 0) return;
      // A release by any other pointer is not this press's, and does not spend
      // it either — it is simply not part of the gesture.
      if (pressBeganOnBackdrop !== event.pointerId) return;
      pressBeganOnBackdrop = null;
      if (pressedOnBackdrop(event.target)) close();
    };
    const onPointerCancel = (event: PointerEvent) => {
      if (pressBeganOnBackdrop === event.pointerId) pressBeganOnBackdrop = null;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("pointercancel", onPointerCancel, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("pointercancel", onPointerCancel, true);
    };
  }, [open, close]);
}
